/** CP Documents panel — upload / list / verify CV, consent, signature, seal. */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, FileArrowUp, CheckCircle, Warning, File as FileIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  listCPProfiles, listCPDocuments, uploadCPDocument, verifyCPDocument,
} from "@/lib/api";
import { CPField, CPTextInput } from "@/pages/wizard/cp/_cpShared";


const DOC_TYPES = ["CV", "Membership", "Consent", "Signature", "Seal",
                    "Independence", "Reliance", "Other"];


export default function CPDocumentsPanel({ projectId, summary, onRefresh }) {
  const qc = useQueryClient();
  const docsQ = useQuery({
    queryKey: ["cp-docs", projectId],
    queryFn:  () => listCPDocuments(projectId),
    enabled:  !!projectId,
  });
  const profilesQ = useQuery({
    queryKey: ["cp-profiles", projectId],
    queryFn:  () => listCPProfiles(projectId),
    enabled:  !!projectId,
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    cp_profile_id: "", doc_type: "CV", file_name: "", content_b64: null,
    mime_type: "", issue_date: "", expiry_date: "",
  });

  const upload = useMutation({
    mutationFn: () => uploadCPDocument(projectId, form),
    onSuccess:  () => {
      toast.success("Document uploaded");
      qc.invalidateQueries({ queryKey: ["cp-docs", projectId] });
      onRefresh?.();
      setOpen(false);
      setForm({ cp_profile_id: "", doc_type: "CV", file_name: "",
                 content_b64: null, mime_type: "", issue_date: "", expiry_date: "" });
    },
    onError: (e) => toast.error(e.message),
  });

  const verify = useMutation({
    mutationFn: (docId) => verifyCPDocument(projectId, docId),
    onSuccess:  () => {
      toast.success("Verified");
      qc.invalidateQueries({ queryKey: ["cp-docs", projectId] });
      onRefresh?.();
    },
    onError: (e) => toast.error(e.message),
  });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({
        ...prev,
        content_b64: reader.result,       // full data URL — backend strips prefix
        file_name:   f.name,
        mime_type:   f.type,
      }));
    };
    reader.readAsDataURL(f);
  };

  const profiles = profilesQ.data?.profiles || [];
  const profileById = (id) => profiles.find((p) => p.id === id);
  const docs = docsQ.data?.documents || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
          {docs.length} document(s) · signed declarations require a verified Signature file
        </div>
        <Button size="sm" onClick={() => setOpen(true)}
                    className="h-7 text-[11px] bg-brand-blue text-white"
                    disabled={profiles.length === 0}
                    data-testid="w-cp-doc-new">
          <Plus size={11} className="mr-1" /> Upload document
        </Button>
      </div>

      <div className="rounded border border-slate-200 overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="text-left px-2 py-1">Type</th>
              <th className="text-left px-2 py-1">CP</th>
              <th className="text-left px-2 py-1">File</th>
              <th className="text-right px-2 py-1">Size</th>
              <th className="text-left px-2 py-1">Expiry</th>
              <th className="text-left px-2 py-1">Verified</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && (
              <tr><td colSpan="7" className="text-center py-4 text-slate-500">No documents yet.</td></tr>
            )}
            {docs.map((d) => (
              <tr key={d.id} className="border-t border-slate-200"
                    data-testid={`w-cp-doc-${d.id}`}>
                <td className="px-2 py-1 font-bold text-slate-800">{d.doc_type}</td>
                <td className="px-2 py-1">{profileById(d.cp_profile_id)?.full_name || "—"}</td>
                <td className="px-2 py-1"><FileIcon size={11} className="inline mr-1 -mt-0.5" />{d.file_name}</td>
                <td className="px-2 py-1 text-right text-slate-500">
                  {d.size_bytes ? `${(d.size_bytes / 1024).toFixed(1)} KB` : "—"}
                </td>
                <td className="px-2 py-1 text-slate-500">
                  {d.expiry_date || "—"}
                  {d.expired && <span className="ml-1 text-red-500"><Warning size={11} className="inline mr-1 -mt-0.5" />expired</span>}
                </td>
                <td className="px-2 py-1">
                  {d.verified
                    ? <span className="text-emerald-600"><CheckCircle size={12} className="inline mr-1 -mt-0.5" />verified</span>
                    : <span className="text-amber-600">pending</span>}
                </td>
                <td className="px-2 py-1">
                  {!d.verified && (
                    <button type="button" onClick={() => verify.mutate(d.id)}
                            className="text-brand-blue text-[10px] underline"
                            data-testid={`w-cp-doc-verify-${d.id}`}>
                      Verify
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload CP document</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <CPField label="CP">
              <Select value={form.cp_profile_id}
                        onValueChange={(v) => setForm({ ...form, cp_profile_id: v })}>
                <SelectTrigger className="h-8 text-[12px]" data-testid="w-cp-doc-cp">
                  <SelectValue placeholder="Select CP" />
                </SelectTrigger>
                <SelectContent>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CPField>
            <CPField label="Document type">
              <Select value={form.doc_type}
                        onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger className="h-8 text-[12px]" data-testid="w-cp-doc-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CPField>
            <CPField label="Issue date">
              <CPTextInput type="date" value={form.issue_date}
                             onChange={(v) => setForm({ ...form, issue_date: v })}
                             testId="w-cp-doc-issue" />
            </CPField>
            <CPField label="Expiry date (optional)">
              <CPTextInput type="date" value={form.expiry_date}
                             onChange={(v) => setForm({ ...form, expiry_date: v })}
                             testId="w-cp-doc-expiry" />
            </CPField>
            <CPField label="File">
              <label className="cursor-pointer inline-flex items-center gap-2 border border-slate-300 rounded p-2 text-[12px] font-mono">
                <FileArrowUp size={14} />
                <span>{form.file_name || "Choose a file…"}</span>
                <input type="file" className="hidden" onChange={onFile}
                        data-testid="w-cp-doc-file" />
              </label>
            </CPField>
          </div>
          <div className="flex justify-end mt-4 gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-brand-blue text-white"
                       disabled={!form.cp_profile_id || !form.content_b64 || upload.isPending}
                       onClick={() => upload.mutate()}
                       data-testid="w-cp-doc-save">
              {upload.isPending ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
